import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AppState } from "../store";
import {
  onDelete,
  onGet,
  onPost,
  onPatch,
  onGetDownload,
  onPostDownload,
} from "../../core/api-service";
import { ErrorState, ResponseState } from "../common-state";
import paths from "../../navigation/paths";
import { setErrorState } from "./common-slice";
import { JDResponse } from "./jd-slice";

export const CandidatestatusIcons = {
  received: "📩",
  read: "✅",
  contacted: "📞",
  interviewed: "🗨️",
  onboarded: "🧑‍💼",
};

export enum CandidateStatus {
  Received = "received",
  Read = "read",
  Contacted = "contacted",
  Interviewed = "interviewed",
  Onboarded = "onboarded",
}

export enum CandidateChannel {
  Internet = "internet",
  Referral = "referral",
  Facebook = "facebook",
  Linkedin = "linkedin",
  Threads = "threads",
  Other = "other",
}

export enum EnglishLevel {
  Beginner = "beginner",
  Elementary = "elementary",
  Intermediate = "intermediate",
  Advanced = "advanced",
  Proficient = "proficient",
}

export interface CandidateCreateRequest {
  name: string;
  email: string;
  phone: string;
  socialLink?: string;
  channel: CandidateChannel;
  status: CandidateStatus;
  currentSalary?: number;
  expectedSalary?: number;
  jdId: string;
  message?: string;
  certificate?: string;
  englishLevel: EnglishLevel;
  applyDate?: Date;
}

export interface CandidateUpdateRequest {
  name?: string;
  email?: string;
  phone?: string;
  socialLink?: string;
  channel?: CandidateChannel;
  status?: CandidateStatus;
  currentSalary?: number;
  expectedSalary?: number;
  jdId?: string;
  message?: string;
  certificate?: string;
  englishLevel?: EnglishLevel;
  applyDate?: Date;
}

export interface GetAllCandidatesRequest {
  status?: CandidateStatus[];
  channel?: CandidateChannel[];
  englishLevel?: EnglishLevel[];
}

export interface CandidateResponse {
  id: string;
  name: string;
  email: string;
  phone: string;
  socialLink?: string;
  cVLink?: string;
  channel: CandidateChannel;
  status: CandidateStatus;
  currentSalary?: number;
  expectedSalary?: number;
  jd: JDResponse;
  message?: string;
  certificate?: string;
  englishLevel: EnglishLevel;
  applyDate?: Date;
}

export interface CandidateState {
  candidateByIdState: { data: CandidateResponse | null; loading: boolean };
  allCandidatesState: {
    data: CandidateResponse[];
    total: number;
    loading: boolean;
  };
  createCandidateState: {
    data: ResponseState<CandidateResponse> | null;
    loading: boolean;
  };
  updateCandidateState: {
    data: ResponseState<CandidateResponse> | null;
    loading: boolean;
  };
  deleteCandidateState: { data: ResponseState<void> | null; loading: boolean };
  downloadCandidateCVState: { data: Blob | null; loading: boolean };
  downloadAllCVsState: { data: Blob | null; loading: boolean };
}

export const candidateByIdStateAction = createAsyncThunk(
  "candidates/id",
  async (params: { id: string }, { dispatch, rejectWithValue }) => {
    try {
      dispatch(setErrorState(null));
      const response = await onGet(`/candidates/${params.id}`);
      return response;
    } catch (error: any) {
      const errorState: ErrorState = {
        message:
          error.response?.data?.message ?? "Error occurred! Please try again.",
        redirect:
          error.response?.status === 401
            ? { destination: paths.UNAUTHORIZED, permanent: true }
            : undefined,
      };
      dispatch(setErrorState(errorState));
      return rejectWithValue(errorState);
    }
  }
);

export const allCandidatesStateAction = createAsyncThunk(
  "candidates/all",
  async (params: GetAllCandidatesRequest, { dispatch, rejectWithValue }) => {
    try {
      dispatch(setErrorState(null));
      const response = await onPost("/candidates/all", params);
      return response;
    } catch (error: any) {
      const errorState: ErrorState = {
        message:
          error.response?.data?.message ?? "Error occurred! Please try again.",
        redirect:
          error.response?.status === 401
            ? { destination: paths.UNAUTHORIZED, permanent: true }
            : undefined,
      };
      dispatch(setErrorState(errorState));
      return rejectWithValue(errorState);
    }
  }
);

export const createCandidateStateAction = createAsyncThunk(
  "candidates/create",
  async (params: { request: FormData }, { dispatch, rejectWithValue }) => {
    try {
      dispatch(setErrorState(null));
      const response = await onPost(
        "/candidates",
        params.request,
        undefined,
        true
      );
      return response;
    } catch (error: any) {
      const errorState: ErrorState = {
        message:
          error.response?.data?.message ?? "Error occurred! Please try again.",
        redirect:
          error.response?.status === 401
            ? { destination: paths.UNAUTHORIZED, permanent: true }
            : undefined,
      };
      dispatch(setErrorState(errorState));
      return rejectWithValue(errorState);
    }
  }
);

export const updateCandidateStateAction = createAsyncThunk(
  "candidates/update",
  async (
    params: { id: string; request: FormData },
    { dispatch, rejectWithValue }
  ) => {
    try {
      dispatch(setErrorState(null));
      const response = await onPatch(
        `/candidates/${params.id}`,
        params.request,
        undefined,
        true
      );
      return response;
    } catch (error: any) {
      const errorState: ErrorState = {
        message:
          error.response?.data?.message ?? "Error occurred! Please try again.",
        redirect:
          error.response?.status === 401
            ? { destination: paths.UNAUTHORIZED, permanent: true }
            : undefined,
      };
      dispatch(setErrorState(errorState));
      return rejectWithValue(errorState);
    }
  }
);

export const deleteCandidateStateAction = createAsyncThunk(
  "candidates/delete",
  async (params: { id: string }, { dispatch, rejectWithValue }) => {
    try {
      dispatch(setErrorState(null));
      const response = await onDelete(`/candidates/${params.id}`);
      return response;
    } catch (error: any) {
      const errorState: ErrorState = {
        message:
          error.response?.data?.message ?? "Error occurred! Please try again.",
        redirect:
          error.response?.status === 401
            ? { destination: paths.UNAUTHORIZED, permanent: true }
            : undefined,
      };
      dispatch(setErrorState(errorState));
      return rejectWithValue(errorState);
    }
  }
);

export const downloadCandidateCVStateAction = createAsyncThunk(
  "candidates/download-cv",
  async (params: { id: string }, { dispatch, rejectWithValue }) => {
    try {
      dispatch(setErrorState(null));
      const response = await onGetDownload(`/candidates/${params.id}/cv`); // Use onDownload for binary
      return response; // Returns the Blob
    } catch (error: any) {
      const errorState: ErrorState = {
        message:
          error.response?.data?.message ??
          "Failed to download CV. Please try again.",
        redirect:
          error.response?.status === 401
            ? { destination: paths.UNAUTHORIZED, permanent: true }
            : undefined,
      };
      dispatch(setErrorState(errorState));
      return rejectWithValue(errorState);
    }
  }
);

export const downloadAllCVsStateAction = createAsyncThunk(
  "candidates/download-cv-all",
  async (params: { candidateIds?: string[] }, { dispatch, rejectWithValue }) => {
    try {
      dispatch(setErrorState(null));
      const response = await onPostDownload("/candidates/cv/all", params);
      return response; // Blob
    } catch (error: any) {
      const errorState: ErrorState = {
        message: error.response?.data ?? "Failed to download CVs.",
      };
      dispatch(setErrorState(errorState));
      return rejectWithValue(errorState);
    }
  }
);

export const candidateSlice = createSlice({
  name: "candidates",
  initialState: {
    candidateByIdState: { data: null, loading: false },
    allCandidatesState: { data: [], total: 0, loading: false },
    createCandidateState: { data: null, loading: false },
    updateCandidateState: { data: null, loading: false },
    deleteCandidateState: { data: null, loading: false },
    downloadCandidateCVState: { data: null, loading: false },
    downloadAllCVsState: { data: null, loading: false },
  } as CandidateState,
  reducers: {
    clearCandidateStates(state, _: PayloadAction) {
      state.candidateByIdState = { data: null, loading: false };
      state.allCandidatesState = { data: [], total: 0, loading: false };
      state.createCandidateState = { data: null, loading: false };
      state.updateCandidateState = { data: null, loading: false };
      state.deleteCandidateState = { data: null, loading: false };
      state.downloadCandidateCVState = { data: null, loading: false };
    },
    clearCreateCandidateState(state, _: PayloadAction) {
      state.createCandidateState = { data: null, loading: false };
    },
    clearUpdateCandidateState(state, _: PayloadAction) {
      state.updateCandidateState = { data: null, loading: false };
    },
    clearDeleteCandidateState(state, _: PayloadAction) {
      state.deleteCandidateState = { data: null, loading: false };
    },
    clearDownloadCandidateCVState(state, _: PayloadAction) {
      state.downloadCandidateCVState = { data: null, loading: false };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(candidateByIdStateAction.pending, (state: CandidateState, _) => {
        state.candidateByIdState.loading = true;
        state.candidateByIdState.data = null;
      })
      .addCase(
        candidateByIdStateAction.fulfilled,
        (state: CandidateState, action) => {
          state.candidateByIdState.loading = false;
          state.candidateByIdState.data = action.payload
            .data as CandidateResponse;
        }
      )
      .addCase(
        candidateByIdStateAction.rejected,
        (state: CandidateState, _) => {
          state.candidateByIdState.loading = false;
        }
      )
      .addCase(allCandidatesStateAction.pending, (state: CandidateState, _) => {
        state.allCandidatesState.loading = true;
      })
      .addCase(
        allCandidatesStateAction.fulfilled,
        (state: CandidateState, action) => {
          state.allCandidatesState.loading = false;
          state.allCandidatesState.data = action.payload
            .data as CandidateResponse[];
          state.allCandidatesState.total = action.payload.meta?.total as number;
        }
      )
      .addCase(
        allCandidatesStateAction.rejected,
        (state: CandidateState, _) => {
          state.allCandidatesState.loading = false;
        }
      )
      .addCase(
        createCandidateStateAction.pending,
        (state: CandidateState, _) => {
          state.createCandidateState.loading = true;
          state.createCandidateState.data = null;
        }
      )
      .addCase(
        createCandidateStateAction.fulfilled,
        (state: CandidateState, action) => {
          state.createCandidateState.loading = false;
          state.createCandidateState.data =
            action.payload as ResponseState<CandidateResponse>;
        }
      )
      .addCase(
        createCandidateStateAction.rejected,
        (state: CandidateState, _) => {
          state.createCandidateState.loading = false;
        }
      )
      .addCase(
        updateCandidateStateAction.pending,
        (state: CandidateState, _) => {
          state.updateCandidateState.loading = true;
          state.updateCandidateState.data = null;
        }
      )
      .addCase(
        updateCandidateStateAction.fulfilled,
        (state: CandidateState, action) => {
          state.updateCandidateState.loading = false;
          state.updateCandidateState.data =
            action.payload as ResponseState<CandidateResponse>;
        }
      )
      .addCase(
        updateCandidateStateAction.rejected,
        (state: CandidateState, _) => {
          state.updateCandidateState.loading = false;
        }
      )
      .addCase(
        deleteCandidateStateAction.pending,
        (state: CandidateState, _) => {
          state.deleteCandidateState.loading = true;
          state.deleteCandidateState.data = null;
        }
      )
      .addCase(
        deleteCandidateStateAction.fulfilled,
        (state: CandidateState, action) => {
          state.deleteCandidateState.loading = false;
          state.deleteCandidateState.data =
            action.payload as ResponseState<void>;
        }
      )
      .addCase(
        deleteCandidateStateAction.rejected,
        (state: CandidateState, _) => {
          state.deleteCandidateState.loading = false;
        }
      )
      .addCase(
        downloadCandidateCVStateAction.pending,
        (state: CandidateState, _) => {
          state.downloadCandidateCVState.loading = true;
          state.downloadCandidateCVState.data = null;
        }
      )
      .addCase(
        downloadCandidateCVStateAction.fulfilled,
        (state: CandidateState, action) => {
          state.downloadCandidateCVState.loading = false;
          state.downloadCandidateCVState.data = action.payload as Blob; // Store as Blob
        }
      )
      .addCase(
        downloadCandidateCVStateAction.rejected,
        (state: CandidateState, _) => {
          state.downloadCandidateCVState.loading = false;
        }
      )
      .addCase(downloadAllCVsStateAction.pending, (state) => {
        state.downloadAllCVsState.loading = true;
        state.downloadAllCVsState.data = null;
      })
      .addCase(downloadAllCVsStateAction.fulfilled, (state, action) => {
        state.downloadAllCVsState.loading = false;
        state.downloadAllCVsState.data = action.payload;
      })
      .addCase(downloadAllCVsStateAction.rejected, (state) => {
        state.downloadAllCVsState.loading = false;
      });
  },
});

export const {
  clearCandidateStates,
  clearCreateCandidateState,
  clearUpdateCandidateState,
  clearDeleteCandidateState,
  clearDownloadCandidateCVState,
} = candidateSlice.actions;

export const getCandidateByIdState = (state: AppState) =>
  state.candidates.candidateByIdState;
export const getAllCandidatesState = (state: AppState) =>
  state.candidates.allCandidatesState;
export const getCreateCandidateState = (state: AppState) =>
  state.candidates.createCandidateState;
export const getUpdateCandidateState = (state: AppState) =>
  state.candidates.updateCandidateState;
export const getDeleteCandidateState = (state: AppState) =>
  state.candidates.deleteCandidateState;
export const getDownloadCandidateCVState = (state: AppState) =>
  state.candidates.downloadCandidateCVState;
export const getDownloadAllCVsState = (state: AppState) =>
  state.candidates.downloadAllCVsState;

export default candidateSlice.reducer;